/**
 * TriggerRmsDocumentRender
 * -------------------------
 * Fires the `rmsRenderTriageLetter` Cloud Function in a fire-and-forget pattern
 * after all paragraphs in a triage letter have been approved.
 *
 * The Cloud Function owns the letter's processingStatus: it sets 'processing'
 * when it starts and 'complete' or 'error' when it finishes. The client must
 * not write that field itself — the security rules reserve it for the server
 * (see letterReviewWriteAllowed in firestore.rules), so a client write is
 * refused and would stop the render from ever being requested.
 *
 * The Documents dashboard page listens for status changes via onSnapshot,
 * so the user sees real-time progress without polling.
 *
 * Usage:
 *   import { TriggerRmsDocumentRender } from './TriggerRmsDocumentRender';
 *   await TriggerRmsDocumentRender({ orgId, patientRecordId, pathway });
 */

import { auth, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

export const TriggerRmsDocumentRender = async ({
  orgId,
  patientRecordId,
  pathway,
}) => {
  console.log('[TriggerRmsDocumentRender] Called with:', { orgId, patientRecordId, pathway });

  const rmsRenderTriageLetter = httpsCallable(functions, 'rmsRenderTriageLetter', {
    timeout: 360000,
  });

  const user = auth.currentUser;
  if (!user || !user.uid) {
    console.warn('[TriggerRmsDocumentRender] User not authenticated');
    return { success: false, error: 'User not authenticated' };
  }

  if (!orgId || !patientRecordId) {
    console.error('[TriggerRmsDocumentRender] Missing orgId or patientRecordId');
    return { success: false, error: 'Missing required parameters' };
  }

  console.log('[TriggerRmsDocumentRender] Calling rmsRenderTriageLetter...');
  rmsRenderTriageLetter({ orgId, patientRecordId, ...(pathway ? { pathway } : {}) })
    .then((result) => {
      console.log('[TriggerRmsDocumentRender] Rendered successfully:', result.data);
    })
    .catch((error) => {
      if (error.code === 'deadline-exceeded') {
        console.log('[TriggerRmsDocumentRender] Client-side timeout — CF continues in background');
        return;
      }
      console.error('[TriggerRmsDocumentRender] Error in CF:', error);
    });

  return { success: true, data: { status: 'processing' } };
};
